/*
 * Hakkımızda (Panelli) — <about-paged>
 * - Kaydırmada aktif paneli bulur: ray başlığı, ray görseli/notu ve mobil şerit güncellenir.
 * - Ray başlığına tıklayınca ilgili panele kaydırır (header + mobil şerit payıyla).
 * - Gradyan bant: kaydırdıkça söner (--p), fareyi yumuşakça takip eder (--mx, --iy),
 *   clip-path ile section sınırlarına (şeffaf header modunda header'ın üstüne kadar) kırpılır.
 */
(() => {
  if (customElements.get('about-paged')) return;

  const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

  class AboutPaged extends HTMLElement {
    connectedCallback() {
      this.panels = Array.from(this.querySelectorAll('[data-rsa-panel]'));
      this.titles = Array.from(this.querySelectorAll('[data-rsa-title]'));
      this.swaps = Array.from(this.querySelectorAll('[data-rsa-mark], [data-rsa-note]'));
      this.subnav = this.querySelector('.rsa__subnav');
      this.subnavLabel = this.querySelector('[data-rsa-subnav-label]');
      this.bg = this.querySelector('[data-rsa-bg]');
      this.header = document.querySelector('.header');
      this.mobile = window.matchMedia('(max-width: 860px)');
      this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.transparent =
        this.hasAttribute('data-transparent-header') &&
        !!this.header &&
        this.header.classList.contains('header--sticky');
      this.active = -1;
      this.frame = 0;

      this.onScroll = this.onScroll.bind(this);
      this.onClick = this.onClick.bind(this);
      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onScroll, { passive: true });
      this.addEventListener('click', this.onClick);

      if (
        this.bg &&
        this.hasAttribute('data-follow') &&
        !this.reduceMotion &&
        window.matchMedia('(pointer: fine)').matches
      ) {
        this.setupFollow();
      }

      this.update();
      this.preloadMarks();

      // #sustainability gibi bir bağlantıyla gelindiyse, düzen oturduktan sonra hizala.
      if (window.location.hash && document.readyState !== 'complete') {
        const index = this.panels.findIndex((panel) => `#${panel.id}` === decodeURIComponent(window.location.hash));
        if (index > -1) window.addEventListener('load', () => this.goTo(index, false), { once: true });
      }
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onScroll);
      if (this.onMove) window.removeEventListener('mousemove', this.onMove);
      if (this.frame) cancelAnimationFrame(this.frame);
      if (this.followFrame) cancelAnimationFrame(this.followFrame);
    }

    onScroll() {
      if (this.frame) return;
      this.frame = requestAnimationFrame(() => {
        this.frame = 0;
        this.update();
      });
    }

    update() {
      const viewport = window.innerHeight;
      const headerBottom = this.header ? Math.max(0, this.header.getBoundingClientRect().bottom) : 0;
      const headerHeight = this.header ? this.header.offsetHeight : 0;
      const subnavHeight = this.mobile.matches && this.subnav ? this.subnav.offsetHeight : 0;

      // --rsa-top: ray/şeridin yapışacağı yer (header'ın o anki alt kenarı).
      // --rsa-header-h: sabit header yüksekliği; panel min-height'ı bununla hesaplanır ki
      // duyuru çubuğu kayarken sayfa yüksekliği oynamasın.
      this.setVar('--rsa-top', `${headerBottom}px`);
      this.setVar('--rsa-header-h', `${headerHeight}px`);
      this.setVar('--rsa-anchor', `${headerHeight + subnavHeight}px`);

      const top = headerBottom + subnavHeight;
      const line = top + (viewport - top) * 0.3;
      let index = 0;
      this.panels.forEach((panel, i) => {
        if (panel.getBoundingClientRect().top <= line) index = i;
      });
      this.setActive(index);

      if (this.bg) this.updateBackground(viewport, headerBottom);
    }

    // Kaydırma her karede çalışıyor; değer değişmediyse stili kirletme.
    setVar(name, value) {
      if (!this.vars) this.vars = {};
      if (this.vars[name] === value) return;
      this.vars[name] = value;
      this.style.setProperty(name, value);
    }

    setActive(index) {
      if (index === this.active) return;
      const initial = this.active === -1;
      this.active = index;

      this.titles.forEach((title) => {
        const on = Number(title.dataset.index) === index;
        title.classList.toggle('is-active', on);
        if (on) title.setAttribute('aria-current', 'true');
        else title.removeAttribute('aria-current');
      });

      this.swaps.forEach((el) => {
        const on = Number(el.dataset.index) === index;
        if (on && el.hidden) {
          el.hidden = false;
          if (!initial) this.replay(el);
        } else if (!on) {
          el.hidden = true;
        }
      });

      const panel = this.panels[index];
      if (this.subnavLabel && panel && this.subnavLabel.textContent !== panel.dataset.title) {
        this.subnavLabel.textContent = panel.dataset.title || '';
        if (!initial) this.replay(this.subnavLabel);
      }
    }

    replay(el) {
      if (this.reduceMotion) return;
      el.classList.remove('rsa-refresh');
      void el.offsetWidth;
      el.classList.add('rsa-refresh');
    }

    onClick(event) {
      const link = event.target.closest('[data-rsa-title]');
      if (!link || !this.contains(link)) return;
      const index = Number(link.dataset.index);
      if (!this.panels[index]) return;
      event.preventDefault();
      this.goTo(index, true);
      if (window.history && window.history.replaceState) {
        window.history.replaceState(null, '', `#${this.panels[index].id}`);
      }
    }

    goTo(index, smooth) {
      const panel = this.panels[index];
      if (!panel) return;

      // Tema header'ı kaydırmanın ilk pikselinde sticky'den fixed'e geçiyor ve içerik
      // header yüksekliği kadar yukarı kayıyor. En tepedeysek önce o geçişi tetikleyip
      // düzen oturduktan sonra hedefi ölçelim, yoksa panel header'ın altında kalıyor.
      if (window.scrollY < 1 && this.header && !this.header.classList.contains('header--scrolled')) {
        window.scrollTo(0, 1);
        requestAnimationFrame(() => requestAnimationFrame(() => this.scrollToPanel(panel, smooth)));
        return;
      }
      this.scrollToPanel(panel, smooth);
    }

    scrollToPanel(panel, smooth) {
      const headerHeight = this.header ? this.header.offsetHeight : 0;
      const subnavHeight = this.mobile.matches && this.subnav ? this.subnav.offsetHeight : 0;
      const target = window.scrollY + panel.getBoundingClientRect().top - headerHeight - subnavHeight;
      window.scrollTo({
        top: Math.max(0, Math.round(target)),
        behavior: smooth && !this.reduceMotion ? 'smooth' : 'auto'
      });
    }

    updateBackground(viewport, headerBottom) {
      const rect = this.getBoundingClientRect();

      // Section'ın başından itibaren kaydırılan mesafe → 0..1 sönme (ease-out).
      const progress = clamp((headerBottom - rect.top) / 560, 0, 1);
      this.bg.style.setProperty('--p', (1 - (1 - progress) * (1 - progress)).toFixed(3));

      let clipTop = Math.max(0, rect.top);
      if (this.transparent) clipTop = Math.max(0, Math.min(clipTop, this.header.getBoundingClientRect().top));
      const clipBottom = Math.max(0, viewport - rect.bottom);
      this.bg.style.clipPath = `inset(${clipTop}px 0 ${clipBottom}px 0)`;
    }

    setupFollow() {
      let targetX = 0;
      let targetY = 0;
      let x = 0;
      let y = 0;
      this.followFrame = 0;

      const step = () => {
        x += (targetX - x) * 0.08;
        y += (targetY - y) * 0.08;
        this.bg.style.setProperty('--mx', `${(90 * x).toFixed(1)}px`);
        this.bg.style.setProperty('--iy', y.toFixed(3));
        const moving = Math.abs(targetX - x) > 0.001 || Math.abs(targetY - y) > 0.001;
        this.followFrame = moving ? requestAnimationFrame(step) : 0;
      };

      this.onMove = (event) => {
        targetX = (event.clientX / window.innerWidth - 0.5) * 2;
        targetY = clamp(event.clientY / window.innerHeight, 0, 1);
        if (!this.followFrame) this.followFrame = requestAnimationFrame(step);
      };
      window.addEventListener('mousemove', this.onMove, { passive: true });
    }

    // Gizli paneller lazy; geçişte görsel geç gelmesin diye boşta önden yükle.
    preloadMarks() {
      const run = () => {
        this.querySelectorAll('.rsa__rail-mark img[loading="lazy"]').forEach((img) => {
          img.loading = 'eager';
        });
      };
      if ('requestIdleCallback' in window) window.requestIdleCallback(run, { timeout: 3000 });
      else window.setTimeout(run, 1500);
    }
  }

  customElements.define('about-paged', AboutPaged);
})();
